<template>
    <div class="compare-text-content pa-relative pa-h-full">
        <PtIcon
            v-if="loading"
            class="pa-absolute pa-bottom-1/2 pa-right-1/2 pa-translate-x-1/2 pa-translate-y-1/2"
            icon="SolidLoadingBars"
        />
        <pre
            ref="result"
            class="pa-m-0 pa-whitespace-pre-wrap pa-font-[inherit] pa-text-[inherit] pa-text-gray-1100"
            style="word-break: break-word;"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, defineEmits, onMounted, onBeforeMount } from 'vue';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { useCompareInit } from '@/composables/use-compare-init';
import { useI18n } from '@/composables/use-i18n';
import CompareService from '@/services/compare-service';
import { Icon as PtIcon, Toast } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';

interface CompareTextData {
    str: string;
    relatedPatentId?: string;
}

const props = withDefaults(defineProps<{
    pattern: string;
    textType: string
}>(), {
    pattern: 'added',
    textType: 'clms',
});

const emit = defineEmits(['loaded']);

const { t } = useI18n();
const { leftId, rightId } = useCompareUrlState();
const { textCompareLang } = useCompareInit();

const result = ref<Element>();

const loading = ref(false);
const diffCount = ref(0);


// from为右侧专利，to为左侧专利
// form表示增加，to表示删除
const fromService = new CompareService(rightId.value);
const toService = new CompareService(leftId.value);

const fromTextData = ref<CompareTextData>({} as CompareTextData);
const toTextData = ref<CompareTextData>({} as CompareTextData);

const closeErrCompare = (text:string) => {
    // @ts-ignore
    Toast({
        message: t(`${props.textType.toUpperCase()}_COMPARE['${text}']`),
        type: 'warning',
        showClose: true,
    });
};

const getCompareText = async () => {
    try {
        const [from, to] = await Promise.all([
            fromService.getPureText(
                props.textType,
                textCompareLang.value?.[props.textType],
            ),
            toService.getPureText(
                props.textType,
                textCompareLang.value?.[props.textType],
            ),
        ]);
        fromTextData.value = from;
        toTextData.value = to;

    } catch (error) {
        console.error(error);
    }
};

const doCompare = async () => {
    const domResult = await fromService.genCompareDom(fromTextData.value.str, toTextData.value.str, fromService.lang, props.pattern);
    result.value.innerHTML = '';
    result.value.appendChild(domResult.fragment);
    diffCount.value = domResult.totalDiffCount;
    const isSame = toTextData.value.relatedPatentId && fromTextData.value.relatedPatentId === toTextData.value.relatedPatentId;
    if (isSame) {
        closeErrCompare('COMPARE_IDENTICAL');
    }
};
const startCompare = async () => {
    loading.value = true;
    emit('loaded', false);
    await getCompareText();

    if (!fromTextData.value.str) {
        closeErrCompare('COMPARE_EMPTY');
        return;
    }

    const aSelfPid = leftId.value;
    const bSelfPid = rightId.value;
    const aClmsStr = toTextData.value.str; // 当前专利文本
    const bClmsStr = fromTextData.value.str; // 对比的专利文本
    const aSelfReplace = toTextData.value.relatedPatentId; // 替代文本的id
    const bSelfReplace = fromTextData.value.relatedPatentId;

    function selfHas(type) {
        if (type === 'a') {
            return aClmsStr && (!aSelfReplace);
        }
        return bClmsStr && (!bSelfReplace);
    }

    // 按照文档表格纯翻译过来 见图 ./compare_warn.png
    // https://confluence.zhihuiya.com/display/doc/Claims+compare+tool

    // 当前专利有文本并且非替代文本 && 对比的专利有文本并且非替代文本
    if (selfHas('a') && selfHas('b')) {
        await doCompare();
        // 当前专利有文本并且非替代文本 && 对比的专利替代文本是当前文本
    } else if (selfHas('a') && bSelfReplace === aSelfPid) {
        // 对比目标没有文本
        closeErrCompare('COMPARE_EMPTY');
        return;
        // 当前专利有文本并且非替代文本 && 对比的专利替代文本非当前专利 && 对比的专利有替代文本
    } else if (selfHas('a') && bSelfReplace !== aSelfPid && bSelfReplace !== bSelfPid) {
        await doCompare();
        // 当前专利的替代文本是对比的专利 && 对比的专利有文本并且非替代文本
    } else if (aSelfReplace === bSelfPid && selfHas('b')) {
        // 两篇文本来自同一篇专利
        closeErrCompare('COMPARE_SAME_PN');
        return;
        // 当前专利的替代文本不是对比专利的替代文本 && 当前专利文本为替代文本 && 对比的专利有文本并且非替代文本
    } else if (aSelfReplace !== bSelfReplace && aSelfReplace !== aSelfPid && selfHas('b')) {
        await doCompare();
        // 当前专利和对比专利替代文本相同
    } else if (aSelfReplace && bSelfReplace && aSelfReplace === bSelfReplace) {
        // 两篇文本来自同一篇专利
        closeErrCompare('COMPARE_SAME_PN');
        return;
    } else {
        await doCompare();
    }

    // 两篇专利文本内容相同
    if (diffCount.value === 0) {
        closeErrCompare('COMPARE_IDENTICAL');
        return;
    }
    loading.value = false;
    emit('loaded', true);
};

onMounted(() => {
    startCompare();
});

onBeforeMount(() => {
    fromService.destroy();
    toService.destroy();
});
</script>


<style lang="scss">
.compare-text-content {
    pre {
        .c-added{
            @apply pa-text-green-600;
        }
        .c-removed {
            @apply pa-text-red-500;
        }
    }
}
</style>
